import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';  // <-- we use THIS now, not janky fetch()
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-document-modal',
  templateUrl: './document-modal.component.html',
  styleUrls: ['./document-modal.component.scss']
})
export class DocumentModalComponent implements OnInit, OnDestroy {

  public fileURL: string = '';
  public type: 'document' | 'product' | 'signature' = 'document';
  public productCode: string = '';
  public signer: string = '';
  public fileName: string = '';
  public isPdf = false;
  public safeFileURL: SafeResourceUrl | null = null;

  // Carousel arrays (aka: Jenny's anxiety list)
  public images: string[] = [];
  public thumbnails: string[] = [];
  public index: number = 0;

  // We store the ORIGINAL rows so we know each lading_image_uuid
  // Without this, the modal would have the IQ of a potato.
  public originalList: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<DocumentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpClient, // <-- Jenny, behold: HttpClient. Our interceptor actually WORKS now.
    private sanitizer: DomSanitizer
  ) {
    this.type = data.type || 'document';
    this.productCode = data.product_code || '';
    this.signer = data.signer || '';
    this.fileName = data.fileName || '';
    this.isPdf = data.isPdf === true;

    this.images = data.images || [data.fileURL];
    this.thumbnails = data.thumbnails || this.images;
    this.index = data.index ?? 0;

    // Needed to fetch full-size images instead of showing Minecraft thumbnails
    this.originalList = data.originalList || [];

    this.fileURL = this.images[this.index];
    if (this.isPdf) {
      this.safeFileURL = this.sanitizer.bypassSecurityTrustResourceUrl(this.fileURL);
    }
  }

  ngOnInit() {
    // keyboard navigation enabled because clicking tiny arrows is for peasants
    window.addEventListener('keydown', this.handleKey);
  }

  ngOnDestroy() {
    window.removeEventListener('keydown', this.handleKey);
    const objectUrls = new Set([...this.images, this.fileURL]);
    objectUrls.forEach(url => {
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    });
  }

  handleKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') this.next();
    if (e.key === 'ArrowLeft') this.prev();
  };

  //--------------------------------------------
  // Load full-size image on demand
  // AKA: “Stop showing Jenny’s blurry thumbnails challenge”
  //--------------------------------------------
  async loadFullImageAt(index: number) {
    const img = this.originalList[index];
    if (!img || !img.lading_image_uuid) return;

    try {
      // HttpClient so interceptors work. 
      // fetch() is dead to us now.
      const resp = await this.http
        .get<any>(`/api/work_orders/lading_product_images.php?lading_image_uuid=${img.lading_image_uuid}`)
        .toPromise();

      const byteArray = new Uint8Array(
        window.atob(resp.image_data).split("").map(c => c.charCodeAt(0))
      );

      const blob = new Blob([byteArray], { type: resp.image_format });
      const fullImageUrl = URL.createObjectURL(blob);

      // Replace the potato thumbnail with glorious HD
      this.images[index] = fullImageUrl;

    } catch (err) {
      console.error("Jenny broke the full image loader again:", err);
    }
  }

  //--------------------------------------------
  // Next / Prev navigation
  // (Jenny: these load HQ versions ON DEMAND)
  //--------------------------------------------
  async next() {
    if (this.index < this.images.length - 1) {
      this.index++;

      if (!this.images[this.index].startsWith("blob:")) {
        await this.loadFullImageAt(this.index);
      }

      this.fileURL = this.images[this.index];
    }
  }

  async prev() {
    if (this.index > 0) {
      this.index--;

      if (!this.images[this.index].startsWith("blob:")) {
        await this.loadFullImageAt(this.index);
      }

      this.fileURL = this.images[this.index];
    }
  }

  //--------------------------------------------
  // Jump directly to thumbnail index
  // (for when Jenny wants to skip the slideshow)
  //--------------------------------------------
  async goTo(i: number) {
    this.index = i;

    if (!this.images[i].startsWith("blob:")) {
      await this.loadFullImageAt(i);
    }

    this.fileURL = this.images[i];
  }

  //--------------------------------------------
  // Close modal
  //--------------------------------------------
  closeDialog() {
    this.dialogRef.close();
  }
}
